/**
 * QGIS-style location bookmarks — save the current camera pose, name it,
 * jump back later. Camera-only on restore (layers / time stay as they are).
 *
 * Backed by `useViewerStore` (`kind: 'bookmark'`) so G18 envelope persistence
 * already covers reload survival. Header popover next to Share link.
 */

import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Icon, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';

export function BookmarkLocationControl() {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [notice, setNotice] = useState('');

  const camera = useViewerStore((s) => s.camera);
  const savedViews = useViewerStore((s) => s.savedViews);
  const bookmarks = useMemo(
    () => savedViews.filter((v) => v.kind === 'bookmark'),
    [savedViews],
  );
  const saveBookmarkLocation = useViewerStore((s) => s.saveBookmarkLocation);
  const restoreBookmarkLocation = useViewerStore((s) => s.restoreBookmarkLocation);
  const removeSavedView = useViewerStore((s) => s.removeSavedView);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSave = () => {
    if (!camera) {
      setNotice('Move the map first, then bookmark this location.');
      return;
    }
    saveBookmarkLocation(name);
    setName('');
    setNotice('Location bookmarked.');
  };

  const handleRestore = (id: string, bookmarkName: string) => {
    restoreBookmarkLocation(id);
    setNotice(`Jumped to ${bookmarkName}.`);
    setOpen(false);
  };

  const handleDelete = (id: string, bookmarkName: string) => {
    removeSavedView(id);
    setNotice(`Deleted ${bookmarkName}.`);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        title="Bookmark location"
        onClick={() => {
          setNotice('');
          setOpen((prev) => !prev);
        }}
        style={headerButtonStyle}
      >
        <Icon name="bookmark" size={16} decorative />
        Bookmarks
        {bookmarks.length > 0 ? (
          <span
            aria-hidden
            style={{
              marginLeft: tokens.space[1],
              minWidth: 18,
              height: 18,
              borderRadius: tokens.radius.chip,
              background: tokens.color.accent.subtle,
              color: tokens.color.ink.primary,
              fontSize: tokens.font.size.xs,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingInline: 4,
            }}
          >
            {bookmarks.length}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Location bookmarks"
          style={{
            position: 'absolute',
            top: `calc(100% + ${tokens.space[1]}px)`,
            right: 0,
            zIndex: 40,
            width: 320,
            maxWidth: 'min(320px, calc(100vw - 24px))',
            padding: tokens.space[3],
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            background: tokens.color.surface[0],
            boxShadow: tokens.elevation[2],
            fontFamily: tokens.font.family.ui,
          }}
        >
          <p
            style={{
              margin: `0 0 ${tokens.space[2]}px`,
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.semibold,
              color: tokens.color.ink.primary,
            }}
          >
            Bookmark location
          </p>
          <p
            style={{
              margin: `0 0 ${tokens.space[3]}px`,
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
              lineHeight: tokens.font.lineHeight.tight,
            }}
          >
            Save the current map position and return to it later — like QGIS spatial bookmarks.
          </p>

          <div style={{ display: 'flex', gap: tokens.space[2], marginBottom: tokens.space[3] }}>
            <label style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                }}
              >
                Name
              </span>
              <input
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSave();
                }}
                placeholder={`Bookmark ${bookmarks.length + 1}`}
                aria-label="Bookmark name"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: tokens.density.touchMin,
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.input,
                  background: tokens.color.surface[1],
                  color: tokens.color.ink.primary,
                  paddingInline: tokens.space[2],
                  fontSize: tokens.font.size.sm,
                }}
              />
            </label>
            <button
              type="button"
              onClick={handleSave}
              disabled={!camera}
              aria-label="Save current location as bookmark"
              title={!camera ? 'Camera not ready yet' : 'Save bookmark'}
              style={{
                ...headerButtonStyle,
                alignSelf: 'end',
                background: camera ? tokens.color.accent.primary : tokens.color.surface[2],
                color: camera ? tokens.color.accent.on : tokens.color.ink.secondary,
                cursor: camera ? 'pointer' : 'not-allowed',
              }}
            >
              <Icon name="bookmark" size={16} decorative />
              Save
            </button>
          </div>

          {bookmarks.length === 0 ? (
            <p
              style={{
                margin: 0,
                fontSize: tokens.font.size.sm,
                color: tokens.color.ink.secondary,
              }}
            >
              No bookmarks yet. Pan to a place, then Save.
            </p>
          ) : (
            <ul
              role="listbox"
              aria-label="Saved location bookmarks"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'grid',
                gap: tokens.space[2],
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {bookmarks.map((bookmark) => (
                <li
                  key={bookmark.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: tokens.space[2],
                    padding: tokens.space[2],
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.md,
                    background: tokens.color.surface[1],
                  }}
                >
                  <button
                    type="button"
                    role="option"
                    onClick={() => handleRestore(bookmark.id, bookmark.name)}
                    aria-label={`Go to bookmark ${bookmark.name}`}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: tokens.color.ink.primary,
                    }}
                  >
                    <strong
                      style={{
                        display: 'block',
                        fontSize: tokens.font.size.sm,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {bookmark.name}
                    </strong>
                    <span
                      style={{
                        fontSize: tokens.font.size.xs,
                        color: tokens.color.ink.secondary,
                      }}
                    >
                      {formatBookmarkMeta(bookmark.camera, bookmark.createdAt)}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(bookmark.id, bookmark.name)}
                    aria-label={`Delete bookmark ${bookmark.name}`}
                    title="Delete bookmark"
                    style={{
                      minHeight: tokens.density.touchMin,
                      minWidth: tokens.density.touchMin,
                      border: tokens.border.thin,
                      borderRadius: tokens.radius.input,
                      background: tokens.color.surface[2],
                      color: tokens.color.ink.primary,
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="delete" size={16} decorative />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {notice ? (
            <p
              role="status"
              style={{
                margin: `${tokens.space[2]}px 0 0`,
                fontSize: tokens.font.size.xs,
                color: tokens.color.semantic.success,
              }}
            >
              {notice}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function formatBookmarkMeta(
  camera: { lonLat?: readonly [number, number]; zoom?: number } | null,
  createdAt: string,
): string {
  const parts: string[] = [];
  if (camera?.lonLat) {
    parts.push(`${camera.lonLat[1].toFixed(4)}, ${camera.lonLat[0].toFixed(4)}`);
  }
  if (typeof camera?.zoom === 'number' && Number.isFinite(camera.zoom)) {
    parts.push(`z${camera.zoom.toFixed(1)}`);
  }
  parts.push(new Date(createdAt).toLocaleDateString());
  return parts.join(' · ');
}

const headerButtonStyle: CSSProperties = {
  minHeight: tokens.density.touchMin,
  padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  background: tokens.color.surface[0],
  color: tokens.color.ink.primary,
  cursor: 'pointer',
  fontSize: tokens.font.size.sm,
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[1],
  whiteSpace: 'nowrap',
};
